import { Component, EventEmitter, Input, Output } from '@angular/core'
import { PersonaFieldTypes } from '../../../../../personas/enums/persona-fields-types.enum'
import { PersonaTypes } from '../../../../../personas/enums/persona-types.enum'
import { Persona } from '../../../../../personas/interfaces/persona.interface'
import { CommonModule } from '@angular/common'

@Component({
  selector: 'ngx-badge-persona-list',
  templateUrl: './badge-persona-list.component.html',
  styleUrls: ['./badge-persona-list.component.scss'],
  standalone: true,
  imports: [CommonModule]
})
export class BadgePersonaListComponent {
  @Input() personas: Persona[] = []
  @Input() selectedPersona?: Persona
  @Output() personaSelected = new EventEmitter<Persona>()
  @Output() backToEvents = new EventEmitter<void>()

  public selectedPersonaId: number | null = null

  public selectPersona(persona: Persona): void {
    this.selectedPersonaId = persona.id || null
    this.selectedPersona = persona
    this.personaSelected.emit(persona)
  }

  public goBackToEvents(): void {
    this.backToEvents.emit()
  }

  public getPersonaDisplayName(persona: Persona): string {
    return persona.name || 'Unnamed Registration Form'
  }

  public hasPersonas(): boolean {
    return this.personas && this.personas.length > 0
  }

  public hasPaymentEnabled(persona: Persona): boolean {
    return persona.enable_payment === true
  }

  public getRegistrationAmount(persona: Persona): number {
    return persona.registration_amount || 0
  }

  public requiresApproval(persona: Persona): boolean {
    return persona.require_approval === true
  }

  public getMaxRegistrations(persona: Persona): number | null {
    return persona.maximum_registrations || null
  }

  public getPersonaType(persona: Persona): string {
    const type = persona.type
    switch (type) {
      case PersonaTypes.VISITOR:
        return 'Visitor'
      case PersonaTypes.VIP:
        return 'VIP'
      case PersonaTypes.GENERIC:
        return 'General'
      case PersonaTypes.GROUP:
        return 'Group'
      case PersonaTypes.MEMBER:
        return 'Member'
      default:
        return 'Registration'
    }
  }

  public getCurrencySymbol(persona: Persona): string {
    return '$' // Could be enhanced with actual currency data
  }

  public getPersonaStatusColor(persona: Persona): string {
    if (this.hasPaymentEnabled(persona)) return 'payment'
    if (this.requiresApproval(persona)) return 'approval'
    return 'free'
  }

  public getPersonaIcon(persona: Persona): string {
    const type = persona.type
    switch (type) {
      case PersonaTypes.VIP:
        return 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,17L7.5,14.5L8.91,13.09L12,16.17L15.09,13.09L16.5,14.5L12,17M12,11A2,2 0 0,1 10,9A2,2 0 0,1 12,7A2,2 0 0,1 14,9A2,2 0 0,1 12,11Z'
      case PersonaTypes.GROUP:
        return 'M16,4C18.11,4 19.81,5.69 19.81,7.8C19.81,9.91 18.11,11.6 16,11.6C13.89,11.6 12.19,9.91 12.19,7.8C12.19,5.69 13.89,4 16,4M16,13.4C18.67,13.4 24,14.73 24,17.4V20H8V17.4C8,14.73 13.33,13.4 16,13.4M8.5,4C10.61,4 12.31,5.69 12.31,7.8C12.31,8.5 12.11,9.14 11.76,9.68C10.32,8.95 9.5,7.9 9.5,6.65C9.5,5.4 10.32,4.35 11.76,3.62C11.11,3.86 8.5,4 8.5,4M8.5,13.4C5.83,13.4 0.5,14.73 0.5,17.4V20H6V17.4C6,16.07 6.5,15.21 7.5,14.61C6.5,13.94 5.83,13.4 8.5,13.4Z'
      case PersonaTypes.MEMBER:
        return 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M7.07,18.28C7.5,17.38 10.12,16.5 12,16.5C13.88,16.5 16.5,17.38 16.93,18.28C15.57,19.36 13.86,20 12,20C10.14,20 8.43,19.36 7.07,18.28M18.36,16.83C16.93,15.09 13.46,14.5 12,14.5C10.54,14.5 7.07,15.09 5.64,16.83C4.62,15.5 4,13.82 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,13.82 19.38,15.5 18.36,16.83M12,6C10.06,6 8.5,7.56 8.5,9.5C8.5,11.44 10.06,13 12,13C13.94,13 15.5,11.44 15.5,9.5C15.5,7.56 13.94,6 12,6M12,11A1.5,1.5 0 0,1 10.5,9.5A1.5,1.5 0 0,1 12,8A1.5,1.5 0 0,1 13.5,9.5A1.5,1.5 0 0,1 12,11Z'
      default:
        return 'M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z'
    }
  }

  // Enhanced methods using the detailed API data
  public getFormFieldsCount(persona: Persona): number {
    return persona.form_elements?.length || 0
  }

  public getRequiredFieldsCount(persona: Persona): number {
    if (!persona.form_elements) return 0
    return persona.form_elements.filter((field) => field.is_required === 1 || field.is_required === 3).length
  }

  public getFormFieldTypes(persona: Persona): string[] {
    if (!persona.form_elements) return []

    const fieldTypeMap: { [key: number]: string } = {
      [PersonaFieldTypes.PLAIN_TEXT]: 'Text',
      [PersonaFieldTypes.EMAIL]: 'Email',
      [PersonaFieldTypes.PHONE]: 'Phone',
      [PersonaFieldTypes.FULLNAME]: 'Full Name',
      [PersonaFieldTypes.COUNTRY]: 'Country',
      [PersonaFieldTypes.UPLOAD]: 'File Upload',
      [PersonaFieldTypes.URL]: 'Website',
      [PersonaFieldTypes.CHECKBOX]: 'Checkbox',
      [PersonaFieldTypes.RADIO]: 'Multiple Choice',
      [PersonaFieldTypes.DATE]: 'Date',
      [PersonaFieldTypes.AGE_GROUP]: 'Age Group',
      [PersonaFieldTypes.GENDER]: 'Gender',
      [PersonaFieldTypes.ORGANIZATION]: 'Organization',
      [PersonaFieldTypes.JOB_TITLE]: 'Job Title',
      [PersonaFieldTypes.NUMERIC]: 'Number',
      [PersonaFieldTypes.LINE_SEPARATOR]: 'Separator',
      [PersonaFieldTypes.INFORMATION_MESSAGE]: 'Info Message',
      [PersonaFieldTypes.WARNING_MESSAGE]: 'Warning',
      [PersonaFieldTypes.STAR_RATING]: 'Star Rating',
      [PersonaFieldTypes.IDENTITY]: 'ID/Passport',
      [PersonaFieldTypes.CITY]: 'City',
      [PersonaFieldTypes.AI_FACIAL_RECOGNITION]: 'AI Face Recognition'
    }

    return persona.form_elements
      .slice(0, 4) // Show first 4 field types
      .map((field) => fieldTypeMap[field.form_element_type_id] || 'Custom Field')
  }

  public hasMoreFields(persona: Persona): boolean {
    return (persona.form_elements?.length || 0) > 4
  }

  public getExtraFieldsCount(persona: Persona): number {
    const total = persona.form_elements?.length || 0
    return Math.max(0, total - 4)
  }

  public hasRegistrationLimits(persona: Persona): boolean {
    return !!(persona.maximum_registrations && persona.maximum_registrations > 0)
  }

  public getPersonaSlug(persona: Persona): string {
    return (persona as any).slug || ''
  }

  public hasPublicDescription(persona: Persona): boolean {
    return !!(persona.public_description && persona.public_description.trim())
  }

  public getPublicDescription(persona: Persona): string {
    return persona.public_description || ''
  }
}
